import { Radio, Typography } from '@mui/material';
import { PmiButton } from 'shell/components/shared/button';
import {
  headCellsDocumentMarriage,
  headCellsDocumentBirth,
} from 'civil-status/constants/head-cells';
import { useFormikContext } from 'formik';
import { useCallback, useMemo } from 'react';
import Table from 'shell/components/shared/table';
import { noop } from 'shell/utils/noop';
import {
  DocumentMarriageFilterFormCertification,
  DocumentMarriageVM,
} from 'civil-status/view-models/document-marriage-list';
import FilterDocument from '../document-list';
import { HttpResponseList } from 'shell/models/http-response';
import { __CIVIL_STATUS_API_URL__ } from 'shell/constants/environment';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { SearchDocument } from 'shell/models/search-document';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import PmiTooltipError from 'shell/components/shared/tooltip-error';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { CertificateType, CertificationFormVM } from 'front-office/view-models/document-birth-form';
import { transformCertificateTitle } from 'front-office/utils/transform-certificate-title';
import { DocumentBirthVM } from 'civil-status/view-models/document-birth-list';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import initialValues from 'front-office/pages/certification-civil-status/initial-values';

interface SearchDocumentProps {
  onBack: () => void;
  onNext: () => void;
}

export default function DocumentMarriageSearchPage({
  onBack,
}: SearchDocumentProps): React.ReactElement {
  const formikForm = useFormikContext<CertificationFormVM>();

  const { userProfile } = useUserProfile();

  const [birthlist, getBirthlist] = useHttpFetch<
    HttpResponseList<SearchDocument>,
    Array<DocumentMarriageVM | DocumentBirthVM>
  >(`${__CIVIL_STATUS_API_URL__}/sima/ricerca-atto`, {
    onBootstrap: false,
    options: {
      errorCatching: true,
      method: 'POST',
      loading: true,
    },
    transformer: (response) => {
      return (
        response.data?.payload?.list?.map((item, index) => {
          const itemResult: DocumentMarriageVM | DocumentBirthVM = {
            identifier: index.toString(),
            registerNumberWife: item.cittadinoId || '',
            surnameWife: item.cittadinoCognome || '',
            nameWife: item.cittadinoNome || '',
            registerNumberHusband: item.cittadino2Id || '',
            surnameHusband: item.cittadino2Cognome || '',
            creationDate: item.dataCreazione || '',
            nameHusband: item.cittadino2Nome || '',
            year: item.registroAnno || '',
            documentNumber: item.attoNumero || '',
            user: item.userLock || '',
            locked: !item.userLock
              ? false
              : item.userLock !== userProfile.datiLogin?.key?.usernameEsteso,
            numReg: item.registroNumero || '',
            parte: item.registroParte?.trim() || '',
            serie: item.registroSerie?.trim() || '',
            code: item.cittadinoId?.toString() || '',
            surname: item.cittadinoCognome || '',
            name: item.cittadinoNome || '',
          };

          return itemResult;
        }) || []
      );
    },
  });

  const data = birthlist.data as Array<DocumentMarriageVM>;

  const handleSearch = (values?: DocumentMarriageFilterFormCertification | null) => {
    formikForm.setValues({
      ...formikForm.values,
      chosenDocument: null,
      ...initialValues.documentSearch,
    });

    getBirthlist({
      data: {
        cittadino2Cognome: values?.surnameHusband || undefined,
        cittadino2CognomeSearchType: values?.surnameHusband
          ? values?.operationSurnameHusband
          : undefined,
        cittadino2Id: values?.matricolaHusband || undefined,
        cittadino2Nome: values?.nameHusband || undefined,
        cittadino2NomeSearchType: values?.nameHusband ? values?.operationNameHusband : undefined,
        attoNumeroA: values?.toDocument || undefined,
        attoNumeroDa: values?.document || values?.fromDocument || undefined,
        cittadinoCognome: values?.surnameWife || undefined,
        cittadinoCognomeSearchType: values?.surnameWife ? values?.operationSurnameWife : undefined,
        cittadinoId: values?.matricolaWife || undefined,
        cittadinoNome: values?.nameWife || undefined,
        cittadinoNomeSearchType: values?.nameWife ? values?.operationNameWife : undefined,
        registroAnno: values?.year || undefined,
        registroNumeroA: values?.toRegistry?.id || undefined,
        registroNumeroDa: values?.registry?.id || values?.fromRegistry?.id || undefined,
        registroParte: values?.portion || undefined,
        registroSerie: values?.serie || undefined,
        tipoAtto: formikForm.values?.certificate?.type,
      },
    }) as ResponsePromise<
      HttpResponseList<SearchDocument>,
      Array<DocumentMarriageVM | DocumentBirthVM>
    >;
  };

  const onChange = useCallback(
    (key, value) => {
      formikForm.setFieldValue('documentSearch', {
        ...(formikForm.values.documentSearch || {}),
        [key]: value,
      });
    },
    [formikForm.setFieldValue, formikForm.values.documentSearch]
  );

  const onChangeValues = useCallback(
    (values) => {
      formikForm.setFieldValue('documentSearch', {
        ...(formikForm.values.documentSearch || {}),
        ...values,
      });
    },
    [formikForm.setFieldValue, formikForm.values.documentSearch]
  );

  const onChangePage = useCallback(
    (page) => {
      onChange('page', page || 0);
    },
    [onChange]
  );

  const onChangeRowsPerPage = useCallback(
    (rowsPerPage) => {
      const newDataset = {
        ...formikForm.values.documentSearch,
        rowsPerPage: rowsPerPage || 0,
        page: 0,
      };

      onChangeValues(newDataset);
    },
    [onChangeValues]
  );

  const lockAct = useMemo(() => {
    let type = '';
    if (formikForm.values?.certificate?.type === CertificateType.nascita) {
      type = 'nascita';
    }

    if (formikForm.values?.certificate?.type === CertificateType.matrimonio) {
      type = 'matrimonio';
    }

    if (formikForm.values?.certificate?.type === CertificateType.morte) {
      type = 'morte';
    }

    if (formikForm.values?.certificate?.type === CertificateType.unioneCivile) {
      type = 'unione-civile';
    }

    if (formikForm.values?.certificate?.type === CertificateType.cittadinanza) {
      type = 'cittadinanza';
    }

    return type;
  }, [formikForm.values?.certificate]);

  const lockDocument = useHttpClient(
    `${__CIVIL_STATUS_API_URL__}atti-${lockAct}-sima/blocca-atto`,
    {
      options: {
        errorCatching: true,
        method: 'PUT',
        loading: true,
      },
    }
  );

  const onVisibilityPress = useCallback(
    (cell): Promise<DocumentMarriageVM> => {
      return new Promise((resolve) => {
        formikForm.setFieldValue('chosenDocument', cell);
        resolve(cell);
      });
    },
    [formikForm.setFieldValue]
  );

  /**
   * The list of document act.
   */
  const list: Array<DocumentMarriageVM | DocumentBirthVM> = useMemo(() => {
    return (
      data?.map((item) => ({
        ...item,
        action: (
          <PmiTooltipError
            show={item.locked}
            message={item.locked ? `Atto bloccato dall'utente ${item.user}` : ''}
          >
            <span>
              <Radio
                checked={formikForm.values?.chosenDocument?.identifier === item.identifier}
                onChange={() => onVisibilityPress(item)}
                value={item.identifier}
                name={`radio-${item.identifier}`}
                inputProps={{ 'aria-label': `Radio ${item.identifier}` }}
              />
            </span>
          </PmiTooltipError>
        ),
      })) || []
    );
  }, [data, onVisibilityPress, formikForm.values?.chosenDocument]);

  const atto = useMemo(() => {
    return transformCertificateTitle(formikForm.values?.certificate);
  }, [formikForm.values?.certificate]);

  return (
    <>
      <Typography
        color="secondary.main"
        style={{
          fontSize: '26px',
        }}
      >
        Ricerca Atto {atto.type}
      </Typography>
      <div className="pmi-my-3">
        <FilterDocument filterDocumentForm={formikForm} handleSearch={handleSearch} />
      </div>
      {data &&
      formikForm.values?.certificate?.type !== CertificateType.matrimonio &&
      formikForm.values?.certificate?.type !== CertificateType.unioneCivile ? (
        list?.length > 0 ? (
          <Table
            ariaLabel="Lista atti"
            page={formikForm.values.documentSearch?.page || 0}
            rowsPerPage={formikForm.values.documentSearch?.rowsPerPage}
            body={list as Array<DocumentBirthVM>}
            count={list.length || 0}
            head={headCellsDocumentBirth}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={noop}
            onEditPress={noop}
            onTrashPress={noop}
            orders={formikForm.values.documentSearch?.orders || {}}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      {data &&
      (formikForm.values?.certificate?.type === CertificateType.matrimonio ||
        formikForm.values?.certificate?.type === CertificateType.unioneCivile) ? (
        list?.length > 0 ? (
          <Table
            ariaLabel="Lista atti"
            page={formikForm.values.documentSearch?.page || 0}
            rowsPerPage={formikForm.values.documentSearch?.rowsPerPage}
            body={list as Array<DocumentMarriageVM>}
            count={list.length || 0}
            head={headCellsDocumentMarriage}
            onSort={noop}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={noop}
            onEditPress={noop}
            onTrashPress={noop}
            orders={formikForm.values.documentSearch?.orders || {}}
            isNotLayoutFixed
            noSort
            hideButtons
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
      <div className="pmi-pt-3 pmi-d-flex pmi-justify-content-center">
        <div className="pmi-w-25 pmi-w-lg-15">
          <PmiButton
            variant="outlined"
            fullWidth
            color="secondary"
            onClick={() => {
              formikForm.setValues({
                ...formikForm.values,
                chosenDocument: null,
                ...initialValues.documentSearch,
              });
              onBack();
            }}
          >
            Indietro
          </PmiButton>
        </div>
        <div className="pmi-pl-2 pmi-w-25 pmi-w-lg-15">
          <PmiTooltipError
            show={!formikForm.values.chosenDocument}
            message={!formikForm.values.chosenDocument ? 'Selezionare un atto per proseguire' : ''}
          >
            <span>
              <PmiButton
                variant="outlined"
                fullWidth
                color="secondary"
                onClick={() => {
                  lockDocument({
                    data: {
                      key: {
                        anno: formikForm.values.chosenDocument?.year || '',
                        numeroAtto: formikForm.values.chosenDocument?.documentNumber || '',
                        numeroRegistro: formikForm.values.chosenDocument?.numReg || '',
                        parte: formikForm.values.chosenDocument?.parte || '',
                        serie1: formikForm.values.chosenDocument?.serie || '',
                        cognome:
                          formikForm.values?.certificate?.type === CertificateType.cittadinanza
                            ? formikForm.values.chosenDocument?.surnameWife
                            : undefined,
                        nome:
                          formikForm.values?.certificate?.type === CertificateType.cittadinanza
                            ? formikForm.values.chosenDocument?.nameWife
                            : undefined,
                      },
                    },
                  });
                }}
                type="submit"
                disabled={!formikForm.values.chosenDocument}
              >
                Avanti
              </PmiButton>
            </span>
          </PmiTooltipError>
        </div>
      </div>
    </>
  );
}

DocumentMarriageSearchPage.displayName = 'home page component';
